import {
  Input,
  Button,
  Controller,
  useForm,
  Typography,
  FormDirtyModal,
  PageModal,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import {
  WttpTemplateByIdQuery,
  WttpTemplateByIdDocument,
  UpdateWttpTemplateScheduleDocument,
  UpdateWttpTemplateScheduleDto,
} from 'src/graphql/generated/graphql';
import 'react-quill/dist/quill.snow.css';

import styles from './index.module.css';
import { useMutation, useQuery } from '@apollo/client';
import {
  QuestionnairesSchedule,
  TemplateQuestionnaireList,
} from '../form/questionnaires-list/questionnaires-list';
import {
  validateWttpTemplateFormQuestionnaires,
  prepareWttpTemplateFormQuestionnaires,
} from '../form/questionnaires-list/utils';
import { useEffect, useState } from 'react';

type Form = {
  name: string;
  questionnaires: QuestionnairesSchedule[];
};

interface TemplateScheduleFormProps {
  onFinish?: () => void;
  id?: string;
  title?:
    | string
    | (string &
        React.ReactElement<any, string | React.JSXElementConstructor<any>>)
    | (string & Iterable<React.ReactNode>)
    | (string & React.ReactPortal);
}

export const TemplateScheduleForm = ({
  onFinish,
  id,
  title,
}: TemplateScheduleFormProps) => {
  const { t } = useTranslation();
  
  const [updateWttpTemplateSchedule, { loading: loadingUpdate }] = useMutation(
    UpdateWttpTemplateScheduleDocument,
  );

  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);

  const { data: wttpTemplateData, loading: loadingWttpTemplate } =
    useQuery<WttpTemplateByIdQuery>(WttpTemplateByIdDocument, {
      variables: {
        wttpTemplateId: id,
        includeQuestionnaires: true,
      },
      fetchPolicy: 'no-cache',
      nextFetchPolicy: 'no-cache',
      skip: !id,
    });

  const template = wttpTemplateData?.wttpTemplateById;
  const templateQuestionnaires = template?.questionnaires || [];

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, defaultValues, isDirty },
    setError,
    reset,
  } = useForm<Form>();

  useEffect(() => {
    if (!template) {
      return;
    }

    reset({
      name: template.name,
      questionnaires: prepareWttpTemplateFormQuestionnaires(
        template.questionnairesWithSchedule,
      ),
    });
  }, [reset, template]);

  const validateQuestionnaires = (value: Array<QuestionnairesSchedule>) => {
    return validateWttpTemplateFormQuestionnaires(value, { setError, t });
  };

  const onSubmit = async (form: Form) => {
    if (!validateQuestionnaires(form.questionnaires) || !template) {
      return;
    }

    await updateWttpTemplateSchedule({
      variables: {
        id: template.id,
        data: {
          questionnaires: form.questionnaires,
        } as UpdateWttpTemplateScheduleDto,
      },
    });

    onFinish?.();
  };

  const onCancel = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onFinish?.();
    }
  };

  const titleElement = title || t('wttp.template.form.create-template');

  return (
    <PageModal open onClose={onCancel} fullWidth title={titleElement}>
      <div className={styles.wrapper}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.fieldsWrapper}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              // Setting this to true here dirties the form causing the form dirty modal to appear even
              // if no modification has been made. This seems to be a design limitation/known issue with
              // ract forms however, removing this still preserves the intended behavior while not dirtying
              // the form
              // disabled={true}
              render={({ field }) => (
                <Input
                  key={defaultValues?.name}
                  label={t('common.name')}
                  placeholder={t('common.name')}
                  error={!!errors.name}
                  {...field}
                  ref={undefined}
                  inputRef={field.ref}
                  value={undefined}
                  defaultValue={field.value}
                  disabled
                />
              )}
            />
            <Controller
              name="questionnaires"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('wttp.template.form.questionnaires')}
                  </Typography>
                  <TemplateQuestionnaireList
                    key={defaultValues?.questionnaires?.length} // Rerender when form default value is changed
                    error={errors.questionnaires?.message}
                    onChange={(data) => setValue('questionnaires', data)}
                    value={field.value}
                    selectableRows="none"
                    loading={loadingWttpTemplate}
                    wttpQuestionnaires={templateQuestionnaires}
                  />
                </>
              )}
            />
          </div>
          <div className="flex justify-end mt-6">
            <Button
              className="mr-2"
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              disabled={loadingUpdate}
            >
              {t('wttp.template.list.actions.save-schedule-button')}
            </Button>
            <Button variant="outlined" color="primary" onClick={onCancel}>
              {t('common.close')}
            </Button>
          </div>
        </form>
      </div>
      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={() => onFinish?.()}
      />
    </PageModal>
  );
};
